/**
 * Pinia store for Macumba CMS content.
 *
 * Strategy:
 *  1. On first use, load hardcoded fallbacks instantly (zero flash).
 *  2. Check localStorage — if cached data is within TTL, use it immediately.
 *  3. Fetch all content from Strapi in parallel; replace store state on success.
 *  4. Write fresh data back to localStorage for next visit.
 *  5. If Strapi is unreachable, hardcoded fallbacks remain — app still works.
 */
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { getCmsClient } from '@/lib/cms'

const CACHE_KEY = 'macumba_cms_v1'
const TTL_MS = 60 * 60 * 1000 // 1 hour

// ── Hardcoded fallbacks (mirror the Strapi seed data) ─────────────────────

const FALLBACK_PLANS = {
  'Super Admin':     { name: 'Super Admin',     icon: 'wand-2',    price_label: 'Internal', monthly_price_usd: null, description: 'Full system access (developer-level)', features: ['Complete admin dashboard access','User management and permissions','Content management system (CMS)','Analytics and reporting tools','System configuration controls','Database management interface'], is_internal: true,  is_popular: false, display_order: 0 },
  'Macumba Editor':  { name: 'Macumba Editor',  icon: 'sparkles',  price_label: 'Free',     monthly_price_usd: null, description: 'CMS and content manager access (staff only)', features: ['Content creation and editing','Destination management','Event and activity publishing','Media library access','Draft and preview capabilities','Collaboration tools'], is_internal: true,  is_popular: false, display_order: 1 },
  'Free Plan':       { name: 'Free Plan',       icon: 'feather',   price_label: 'Free',     monthly_price_usd: 0,    description: 'Limited access, rewards eligible', features: ['Browse destinations and activities','Save up to 5 trips','Basic trip planning tools','Rewards program access','Community forum viewing','Mobile app access'], is_internal: false, is_popular: false, display_order: 2 },
  'Standard Plan':   { name: 'Standard Plan',   icon: 'shell',     price_label: '$15/month', monthly_price_usd: 15,  description: 'Unlimited searches, posts destinations, joins community', features: ['Unlimited trip searches','Save unlimited trips','Post destination reviews','Community forum participation','Priority customer support','Advanced filtering options','Trip sharing with friends'], is_internal: false, is_popular: false, display_order: 3 },
  'Premium Plan':    { name: 'Premium Plan',    icon: 'gem',       price_label: '$25/month', monthly_price_usd: 25,  description: 'Full community access, post stories/ads, find travel buddies', features: ['All Standard features','Create and share travel stories','Find travel buddies and groups','Exclusive community events','Early access to new features','Ad-free experience','Premium destination guides','Personalized recommendations'], is_internal: false, is_popular: true,  display_order: 4 },
  'Business Plan':   { name: 'Business Plan',   icon: 'briefcase', price_label: '$40/month', monthly_price_usd: 40,  description: 'Business listings, ads, audience reports', features: ['Business profile and listings','Advertising placement tools','Audience analytics dashboard','Lead generation features','SEO optimization tools','Multi-location management','API access for integrations','Dedicated account manager'], is_internal: false, is_popular: false, display_order: 5 },
}

const FALLBACK_REGIONS = [
  { name: 'Southeast Asia',  description: 'Popular destinations across the region',    display_order: 0 },
  { name: 'Europe',          description: 'Explore the European continent',            display_order: 1 },
  { name: 'Scandinavia',     description: 'Nordic countries and destinations',          display_order: 2 },
  { name: 'Mediterranean',   description: 'Coastal destinations and islands',           display_order: 3 },
  { name: 'Middle East',     description: 'Explore Middle Eastern destinations',        display_order: 4 },
  { name: 'South America',   description: 'Explore the South American continent',      display_order: 5 },
  { name: 'Central America', description: 'Destinations across Central America',       display_order: 6 },
  { name: 'Caribbean',       description: 'Island destinations and cruises',            display_order: 7 },
  { name: 'East Africa',     description: 'Safari and coastal destinations',           display_order: 8 },
  { name: 'West Africa',     description: 'Explore West African destinations',         display_order: 9 },
]

const FALLBACK_ACTIVITIES = [
  { title: 'Read a destination guide', description: 'Discover hidden gems and earn for every guide you finish.', points: 20, display_order: 0 },
  { title: 'Add a new destination',    description: 'Share your recent discoveries with the community.',         points: 45, display_order: 1 },
  { title: 'Rate a destination',       description: 'Help fellow travelers by rating places you have visited.',  points: 30, display_order: 2 },
  { title: 'Invite a friend',          description: 'Bring friends along and both of you will earn points.',     points: 80, display_order: 3 },
]

const FALLBACK_FOOTER_SECTIONS = [
  { title: 'Quick Links', display_order: 0, links: [{ label: 'Home', url: '/' },{ label: 'Destinations', url: '#destinations' },{ label: 'Tours', url: '#tours' },{ label: 'About Us', url: '#about' },{ label: 'Contact', url: '#contact' }] },
  { title: 'Support',     display_order: 1, links: [{ label: 'FAQ', url: '#' },{ label: 'Terms & Conditions', url: '#' },{ label: 'Privacy Policy', url: '#' },{ label: 'Travel Insurance', url: '#' },{ label: 'Booking Policy', url: '#' }] },
]

const FALLBACK_SITE_CONFIG = {
  company_name:  'MacumbaTravel',
  tagline:       'Your trusted partner for unforgettable travel experiences around the world.',
  address:       'Global HQ: 123 Travel Street, Sydney',
  email:         'info@macumbatravel.com',
  phone:         '+61 2 1234 5678',
  phone_label:   '24/7 Global Support',
  facebook_url:  '#',
  twitter_url:   '#',
  instagram_url: '#',
}

// ── Store ──────────────────────────────────────────────────────────────────

export const useCmsStore = defineStore('cms', () => {
  const plans          = ref({ ...FALLBACK_PLANS })
  const regions        = ref([...FALLBACK_REGIONS])
  const activities     = ref([...FALLBACK_ACTIVITIES])
  const footerSections = ref([...FALLBACK_FOOTER_SECTIONS])
  const siteConfig     = ref({ ...FALLBACK_SITE_CONFIG })
  const banner         = ref(null)
  const isLoaded       = ref(false)
  let   fetchPromise   = null

  // ── Cache helpers ──────────────────────────────────────────────────────

  function _loadFromCache() {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return false
      const { data, timestamp } = JSON.parse(raw)
      if (Date.now() - timestamp > TTL_MS) return false
      if (data.plans)          plans.value          = data.plans
      if (data.regions)        regions.value        = data.regions
      if (data.activities)     activities.value     = data.activities
      if (data.footerSections) footerSections.value = data.footerSections
      if (data.siteConfig)     siteConfig.value     = data.siteConfig
      banner.value = data.banner ?? null
      isLoaded.value = true
      return true
    } catch {
      return false
    }
  }

  function _saveToCache() {
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify({
        timestamp: Date.now(),
        data: {
          plans:          plans.value,
          regions:        regions.value,
          activities:     activities.value,
          footerSections: footerSections.value,
          siteConfig:     siteConfig.value,
          banner:         banner.value,
        },
      }))
    } catch { /* quota exceeded or private mode — silently ignore */ }
  }

  // ── Main fetch ────────────────────────────────────────────────────────

  async function fetchAll() {
    // Deduplicate concurrent calls
    if (fetchPromise) return fetchPromise
    // Use cache if fresh
    if (_loadFromCache()) return

    fetchPromise = (async () => {
      try {
        const client = getCmsClient()

        const [plansRes, regionsRes, activitiesRes, footerRes, configRes, bannerRes] =
          await Promise.allSettled([
            client.collection('subscription-plans').find({ sort: ['display_order:asc'] }),
            client.collection('popular-regions').find({ sort: ['display_order:asc'] }),
            client.collection('reward-activities').find({ sort: ['display_order:asc'] }),
            client.collection('footer-sections').find({ sort: ['display_order:asc'] }),
            client.single('site-config').find(),
            client.single('announcement-banner').find(),
          ])

        if (plansRes.status === 'fulfilled' && plansRes.value?.length) {
          plans.value = Object.fromEntries(plansRes.value.map((p) => [p.name, p]))
        }
        if (regionsRes.status === 'fulfilled' && regionsRes.value?.length) {
          regions.value = regionsRes.value
        }
        if (activitiesRes.status === 'fulfilled' && activitiesRes.value?.length) {
          activities.value = activitiesRes.value
        }
        if (footerRes.status === 'fulfilled' && footerRes.value?.length) {
          footerSections.value = footerRes.value
        }
        if (configRes.status === 'fulfilled' && configRes.value) {
          siteConfig.value = configRes.value
        }
        if (bannerRes.status === 'fulfilled' && bannerRes.value?.is_active) {
          banner.value = bannerRes.value
        }

        isLoaded.value = true
        _saveToCache()
      } catch (err) {
        console.warn('[CMS] Fetch failed, using fallbacks:', err?.message ?? err)
      } finally {
        fetchPromise = null
      }
    })()

    return fetchPromise
  }

  /** Force a cache bust and re-fetch (useful after an editor saves content). */
  function invalidate() {
    localStorage.removeItem(CACHE_KEY)
    fetchPromise = null
    isLoaded.value = false
  }

  return { plans, regions, activities, footerSections, siteConfig, banner, isLoaded, fetchAll, invalidate }
})
